// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
// import Checkbox from '../Checkbox';
import CodeEditor from '../CodeEditor';
import Editable from '../Editable';
import EditableMulti from '../EditableMulti';
import Modal from './Modal';

const LANGUAGES = [
    {
        name: 'Python',         // Displayed to the user
        tag: 'py',              // Value from the API
        editorMode: 'python'    // Value for Ace Editor https://github.com/securingsincity/react-ace/blob/master/docs/Modes.md
    },
    // Add new language support here
];

export default class EditScript extends Modal {

    state = {
        name: '',
        language: LANGUAGES[0],
        requirements: [],
        source: '',
        schedule: {
            weekly: true,
            dates: [],
            days: [],
            hours: [],
        },
    }

    componentDidMount = () => {
        // Find our current language based on the script
        let foundLanguage = false;
        for (let i = 0; i < LANGUAGES.length; i++) {
            if (this.props.script.language === LANGUAGES[i].tag) {
                foundLanguage = true;
                this.setState({
                    name: this.props.script.name,
                    language: LANGUAGES[i],
                    schedule: this.props.script.schedule,
                    requirements: this.props.script.requirements,
                    source: this.props.script.source,
                });
                break;
            }
        }

        // We didn't find our language, default to python
        if (!foundLanguage) {
            this.setState({
                name: this.props.script.name,
                language: LANGUAGES[0],
                schedule: this.props.script.schedule,
                requirements: this.props.script.requirements,
                source: this.props.script.source,
            });
        }
    }

    save = async () => {
        const script = {
            _id: this.props.script._id,
            name: this.state.name,
            requirements: this.state.requirements,
            source: this.state.source,
            language: this.state.language.tag,
            schedule: this.state.schedule,
        }
        
        if (!script.name) {
            return toast.error('Script name cannot be empty!');
        }

        await this.props.saveScript(script);
        this.props.modal();
    }

    sortWeekdays = (days) => {
        // This makes sure that the weekdays appear in the correct order
        // regardless of what order the user clicks them

        // This closure will be used by array.sort
        function getWeekdayValue(day) {
            switch (day) {
                case ('Mon'): return 0;
                case ('Tue'): return 1;
                case ('Wed'): return 2;
                case ('Thu'): return 3;
                case ('Fri'): return 4;
                case ('Sat'): return 5;
                case ('Sun'): return 6;
                default: return 7;
            }
        }

        return days.sort((day1, day2) => getWeekdayValue(day1) - getWeekdayValue(day2));
    }

    toTime = (hours) => {
        // This method converts hours (100, 200, etc.) to pretty AM/PM display time

        let hour = hours / 100;
        let isPM = false;

        // Afternoon times should be PM
        if (hour >= 12) {
            isPM = true;
        }

        // Make midnight be 12 AM
        if (hour === 0 || hour === 24) {
            hour = 12;
            isPM = false;
        }

        // PM time
        if (hour > 12) {
            hour -= 12;
        }

        return `${hour} ${isPM ? 'PM' : 'AM'}`;
    }

    generateTimes = (startingHour) => {
        // Will create a list of option objects for a Select component
        // startingHour will be the first option in the list - must be military time (100, 200, etc.)

        let options = [];
        let hoursLeft = 24;
        let hour = startingHour;

        while (hoursLeft > 0) {
            options.push({
                label: this.toTime(hour),
                value: hour,
            });

            // Increment to our next hour, or around to midnight
            hour += 100;
            if (hour >= 2400) {
                hour = 0;
            }

            hoursLeft--;
        }

        return options;
    }

    renderContent = () => {

        return <div className="modal-content modal-center">

            <img src={"https://static.stax.ai/images/icons/script.png"} alt="Script Icon"/>
            <Editable
                style={{
                    fontSize: 24.0,
                    width: '50%',
                    margin: 'auto',
                }}
                value={this.state.name}
                setValue={(newName) => this.setState({
                    name: newName,
                })}
                fieldName="script name"
            />

            <h2>Scripting Language</h2>
            <Select
                className="select-container"
                classNamePrefix="select"
                value={{
                    label: this.state.language.name,
                    value: this.state.language,
                }}
                options={LANGUAGES.map((lang) => ({
                    label: lang.name,
                    value: lang,
                }))}
                isSearchable={false}
                placeholder="Select scripting language..."
                onChange={(option) => {
                    if (option) {
                        this.setState({
                            language: option,
                        });
                    }
                }}
            />

            <h2>Requirements</h2>
            <EditableMulti
                className='edit-script'
                placeholder='Package names...'
                value={this.state.requirements.map((req) => ({
                    label: req,
                    value: req,
                }))}
                hideMenu={true}
                onChange={(selections) => {
                    if (selections) {
                        this.setState({
                            requirements: selections.map((selection => selection.value))
                        });
                    } else {
                        this.setState({
                            requirements: [],
                        });
                    }
                }}
            />

            <h2>Source Code</h2>
            <CodeEditor
                name="script-editor"
                style={{
                    width: '100%',
                }}
                className="config-code"
                mode={this.state.language.editorMode}
                theme={this.props.user?.enableDarkMode ? "twilight" : "xcode"}
                value={this.state.source}
                onChange={source => this.setState({
                    source: source,
                })}
                height={500}
            />

            <h2>Schedule Script</h2>
        
            {/* <p>Scripts can either run on specific weekdays or specific days of the month.</p>

            <Checkbox
                className="settings-checkbox"
                label="Run on weekdays?"
                checked={this.state.schedule.weekly}
                onToggle={() => this.setState((oldState) => ({
                    schedule: {
                        ...oldState.schedule,
                        weekly: !oldState.schedule.weekly,
                    },
                }))}
            /> */}

            { this.state.schedule.weekly ? <>
            
                <p>Which days of the week should the script run on? (You can add multiple)</p>
                <Select
                    className="select-container"
                    classNamePrefix="multi-select"
                    value={this.state.schedule.days.map((e) => ({
                        label: e,
                        value: e,
                    }))}
                    isMulti
                    options={[{
                        label: 'Monday',
                        value: 'Mon',
                    },{
                        label: 'Tuesday',
                        value: 'Tue',
                    },{
                        label: 'Wednesday',
                        value: 'Wed',
                    },{
                        label: 'Thursday',
                        value: 'Thu',
                    },{
                        label: 'Friday',
                        value: 'Fri',
                    },{
                        label: 'Saturday',
                        value: 'Sat',
                    },{
                        label: 'Sunday',
                        value: 'Sun',
                    }]}
                    isSearchable={false}
                    placeholder="Days of the week..."
                    closeMenuOnSelect={false}
                    onChange={(selections) => {
                        if (selections) {
                            this.setState((oldState) => ({
                                schedule: {
                                    ...oldState.schedule,
                                    days: this.sortWeekdays(selections.map((e) => e.value)),
                                }
                            }));
                        } else {
                            // Clear days
                            this.setState((oldState) => ({
                                schedule: {
                                    ...oldState.schedule,
                                    days: [],
                                },
                            }));
                        }
                    }} 
                />

            </> : <>
            
                <h3>Which days of the month should the script run on? (Enter a comma-separated list)</h3>

                <EditableMulti
                    className='edit-script'
                    placeholder='Days of the month (1, 2, 31, etc...)'
                    value={this.state.schedule.dates.map((date) => ({
                        label: date,
                        value: date,
                    }))}
                    hideMenu={true}
                    onChange={(selections) => {
                        if (selections) {
                            // Ensure every value is a integer 1 to 31
                            for (let s = selections.length - 1; s >= 0; s--) {
                                
                                let isValid = false;

                                const number = Number(selections[s].value);
                                isValid = (!isNaN(number) && number >= 1 && number <= 31 && number % 1 === 0);

                                // Remove invalid dates from our selection list
                                if (!isValid) {
                                    selections.splice(s, 1);
                                }
                            }

                            this.setState((oldState) => ({
                                schedule: {
                                    ...oldState.schedule,
                                    dates: selections.map((selection => selection.value)).sort((a, b) => a - b),
                                },
                            }));
                        } else {
                            // Clear the dates we've selected
                            this.setState((oldState) => ({
                                schedule: {
                                    ...oldState.schedule,
                                    dates: [],
                                }
                            }));
                        }
                    }}
                />

            </>}

            <br/>
            <h3>On the specified days, what hours should the script run on? (You can add multiple)</h3>

            <Select
                className="select-container"
                classNamePrefix="multi-select"
                value={this.state.schedule.hours.map((e) => ({
                    label: this.toTime(e),
                    value: e,
                }))}
                isMulti
                options={this.generateTimes(600)}
                isSearchable={false}
                placeholder="Time of day..."
                closeMenuOnSelect={false}
                onChange={(selections) => {
                    if (selections) {
                        this.setState((oldState) => ({
                            schedule: {
                                ...oldState.schedule,
                                hours: selections.map((e) => e.value).sort((a, b) => a - b),
                            }
                        }));
                    } else {
                        // Clear days
                        this.setState((oldState) => ({
                            schedule: {
                                ...oldState.schedule,
                                hours: [],
                            },
                        }));
                    }
                }}
            />

            <br/>

            <div
                className="button"
                style={{
                    float: "none",
                    backgroundImage: "url(https://static.stax.ai/images/icons/reviewed.png)",
                }}
                onClick={this.save}
            >Save and Close</div>

            <br/><br/><br/>
        </div>;
    }

}